import {Translatable} from 'common/i18n';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DashboardCardId} from 'common/report/util/saved-entities/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {CohortGroupEntry} from 'common/query-builder/query-entry-section/types';
import {PersistedBoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {Segmentation} from 'common/types/reports/query';
import {ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {ImpactReportAttrs} from 'apps/impact/src/models/report';
import {JqlBookmarkParams} from 'apps/jql-console/src/jql-console/types';
import {HeatMap, HeatMapCollection} from 'common/widgets/heat-maps/types';

import type {DashboardOrigin} from '../util/bookmark-overrides';
import {FeatureRolloutName} from '../util/feature-sampling';
import {FlowsBookmark} from '../flows/models/types';
import {FunnelBookmark} from '../funnels/models/types';

// These values must match template types in webapp/dashboard/models.py
export enum TemplateType {
  KPI = `KPI`,
  FEATURE_LAUNCH = `Feature Launch`,
  LIFECYCLE_COHORT_ANALYSIS = `Lifecycle Cohort Analysis`,
  MARKETING_KPI = `Marketing KPI`,
  E_COMMERCE = `E-Commerce`,
  WEB_ANALYTICS = `Web Analytics`,
  STARTER_BOARD = `Starter Board`,
  BENCHMARK_KPI = `Benchmark KPI`,
  B2B = `B2B`,
  SAAS_COMPANY_KPI = `SAAS Company KPIs`,
  AI_COMPANY_KPI = `AI Company KPIs`,
}

// These values must match benchmark card types in webapp/dashboard/models.py
export enum BenchmarkCardType {
  MOM_GROWTH = `mom_growth`,
  AVG_SESSION = `avg_session`,
  ONE_WEEK_RETENTION = `one_week_retention`,
  USER_STICKINESS = `user_stickiness`,
}

export type BookmarkParamsUnionType =
  | InsightsBookmarkParams
  | FunnelBookmark
  | FlowsBookmark
  | ImpactReportAttrs
  | JqlBookmarkParams;

export type TemplateField =
  | KPITemplateFields
  | ProductLaunchTemplateFields
  | LifecycleCohortAnalysisTemplateFields
  | MarketingKPITemplateFields
  | ECommerceTemplateFields
  | WebAnalyticsTemplateFields
  | B2BTemplateFields
  | SaaSCompanyKpiTemplateFields
  | AICompanyKpiTemplateFields;

export enum KPITemplateFields {
  SIGN_UP = `Sign Up`,
  VALUE_MOMENT = `Value Moment`,
}

export enum ProductLaunchTemplateFields {
  FEATURE_NAME = `Feature Name`,
  LAUNCH_DATE = `Launch Date`,
  NEW_FEATURE = `New Feature`,
  FEATURE_VALUE_MOMENT = `Feature Value Moment`,
}

export enum LifecycleCohortAnalysisTemplateFields {
  VALUE_MOMENT = `Value Moment`,
}

export enum MarketingKPITemplateFields {
  ACTIVATION_EVENT = `Activation Event`,
  CONVERSION_EVENT = `Conversion Event`,
  AD_SPEND_EVENT = `Ad Spend Event`,
  AD_COST_PROPERTY = `Ad Cost`,
  AD_CLICKS_PROPERTY = `Ad Clicks`,
  AD_IMPRESSIONS_PROPERTY = `Ad Impressions`,
  CONVERSION_PURCHASE_AMOUNT_PROPERTY = `Conversion Purchase Amount`,
}

export enum ECommerceTemplateFields {
  PURCHASE_EVENT = `Purchase`,
  ADD_TO_CART_EVENT = `Add to Cart`,
  PRODUCT_VIEWED_EVENT = `Product Viewed`,
  CART_PROPERTY = `Cart Property`,
  CART_PROPERTY_2 = `Cart Property 2`,
  PRICE_KEY = `Price Key`,
  CATEGORY_KEY = `Category Key`,
}

export enum WebAnalyticsTemplateFields {
  PAGE_LOADED_EVENT = `Page Loaded`,
  PAGE_NAME_PROPERTY = `Page Name`,
}

export enum B2BTemplateFields {
  DATA_GROUP_ID = `Data Group`,
  ACTIVATION_EVENT = `Activation Event`,
  ACCOUNT_NAME_PROPERTY_FIELD = `Account Name`,
  ARR_PROPERTY_FIELD = `Account ARR`,
  SUBSCRIPTION_PLAN_TYPE_PROPERTY_FIELD = `Subscription Plan Type`,
}

export enum SaaSCompanyKpiTemplateFields {
  DATA_GROUP_ID = `Data Group`,
  MRR_PROPERTY_FIELD = `MRR`,
  PLAN_TYPE_PROPERTY_FIELD = `Subscription Plan Type`,
  SEGMENT_PROPERTY_FIELD = `Segment`,
}

export enum AICompanyKpiTemplateFields {
  AI_SUBMIT_PROMPT_EVENT_FIELD = `Submit Prompt`,
  AI_PROMPT_PROPERTY_FIELD = `Prompt`,
  AI_RESPONSE_SENT_EVENT_FIELD = `Response Sent`,
  AI_RESPONSE_COST_PROPERTY_FIELD = `API Cost`,
  AI_RESPONSE_TIME_PROPERTY_FIELD = `API Response Time`,
  AI_TOKENS_USED_PROPERTY_FIELD = `Tokens Used`,
  AI_SIGN_UP_EVENT_FIELD = `Sign Up`,
  AI_START_AGENT_EVENT_FIELD = `Launch AI`,
  AI_AGENT_DISMISSED_EVENT_FIELD = `Agent Dismissed`,
  AI_API_ERROR_EVENT_FIELD = `API Error`,
  AI_USER_FEEDBACK_EVENT_FIELD = `User Feedback`,
  AI_USER_FEEDBACK_SENTIMENT_PROPERTY_FIELD = `User Feedback Sentiment`,
  AI_USER_FEEDBACK_TEXT_PROPERTY_FIELD = `User Feedback Text`,
  AI_CONVERSION_EVENT_FIELD = `Conversion Event`,
}

export type TemplateSpecsKeys =
  | TemplateType.KPI
  | TemplateType.FEATURE_LAUNCH
  | TemplateType.LIFECYCLE_COHORT_ANALYSIS
  | TemplateType.MARKETING_KPI
  | TemplateType.E_COMMERCE
  | TemplateType.WEB_ANALYTICS
  | TemplateType.B2B
  | TemplateType.SAAS_COMPANY_KPI
  | TemplateType.AI_COMPANY_KPI;

export type TemplateSpecsEntry = {
  title: string;
  type: TemplateType;
  imageId: string;
  demoBoardLink?: string;
  requiredFields: TemplateField[];
  keywords: string[];
  requiredFieldsCheck?: () => boolean;
  /**
   * Optionally include a feature flag that's required to display the template.
   */
  featureRolloutName?: FeatureRolloutName;
  titleBadge?: string;
};

// TODO (@rhoeuns) combine with TemplateSpecs as one const after localization has applied to search and RHS components
export const TemplateSpecsTranslationKey: Record<
  TemplateSpecsKeys,
  {title: Translatable; description: Translatable; alternativeDescription?: Translatable}
> = {
  [TemplateType.KPI]: {
    title: ({t}) => t(`boards.template-setup.kpi.title`),
    description: ({t}) => t(`boards.template-setup.kpi.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.kpi.alternative-description`),
  },
  [TemplateType.FEATURE_LAUNCH]: {
    title: ({t}) => t(`boards.template-setup.feature-launch.title`),
    description: ({t}) => t(`boards.template-setup.feature-launch.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.feature-launch.alternative-description`),
  },
  [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: {
    title: ({t}) => t(`boards.template-setup.lifecycle.title`),
    description: ({t}) => t(`boards.template-setup.lifecycle.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.lifecycle.alternative-description`),
  },
  [TemplateType.E_COMMERCE]: {
    title: ({t}) => t(`boards.template-setup.ecommerce.title`),
    description: ({t}) => t(`boards.template-setup.ecommerce.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.ecommerce.alternative-description`),
  },
  [TemplateType.MARKETING_KPI]: {
    title: ({t}) => t(`boards.template-setup.marketing.title`),
    description: ({t}) => t(`boards.template-setup.marketing.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.marketing.alternative-description`),
  },
  [TemplateType.WEB_ANALYTICS]: {
    title: ({t}) => t(`boards.template-setup.web-analytics.title`),
    description: ({t}) => t(`boards.template-setup.web-analytics.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.web-analytics.alternative-description`),
  },
  [TemplateType.B2B]: {
    title: ({t}) => t(`boards.template-setup.b2b.title`),
    description: ({t}) => t(`boards.template-setup.b2b.description`),
  },
  [TemplateType.SAAS_COMPANY_KPI]: {
    title: ({t}) => t(`boards.template-setup.saas-company-kpi.title`),
    description: ({t}) => t(`boards.template-setup.saas-company-kpi.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.saas-company-kpi.alternative-description`),
  },
  [TemplateType.AI_COMPANY_KPI]: {
    title: ({t}) => t(`boards.template-setup.ai-company-kpi.title`),
    description: ({t}) => t(`boards.template-setup.ai-company-kpi.description`),
    alternativeDescription: ({t}) => t(`boards.template-setup.ai-company-kpi.alternative-description`),
  },
};

// this is duplicated from the above so that we can slowly move to returning all templates from
// the back-end, this is a way to more easily parse the translations
export const TemplateTitleMapping: Record<string, Translatable> = {
  'boards.template-setup.kpi.title': ({t}) => t(`boards.template-setup.kpi.title`),
  'boards.template-setup.feature-launch.title': ({t}) => t(`boards.template-setup.feature-launch.title`),
  'boards.template-setup.lifecycle.title': ({t}) => t(`boards.template-setup.lifecycle.title`),
  'boards.template-setup.ecommerce.title': ({t}) => t(`boards.template-setup.ecommerce.title`),
  'boards.template-setup.marketing.title': ({t}) => t(`boards.template-setup.marketing.title`),
  'boards.template-setup.web-analytics.title': ({t}) => t(`boards.template-setup.web-analytics.title`),
  'boards.template-setup.saas-company-kpi.title': ({t}) => t(`boards.template-setup.saas-company-kpi.title`),
  'boards.template-setup.ai-company-kpi.title': ({t}) => t(`boards.template-setup.ai-company-kpi.title`),
};

export const TemplateDescriptionMapping: Record<string, Translatable> = {
  'boards.template-setup.kpi.alternative-description': ({t}) => t(`boards.template-setup.kpi.alternative-description`),
  'boards.template-setup.feature-launch.alternative-description': ({t}) =>
    t(`boards.template-setup.feature-launch.alternative-description`),
  'boards.template-setup.lifecycle.alternative-description': ({t}) =>
    t(`boards.template-setup.lifecycle.alternative-description`),
  'boards.template-setup.ecommerce.alternative-description': ({t}) =>
    t(`boards.template-setup.ecommerce.alternative-description`),
  'boards.template-setup.marketing.alternative-description': ({t}) =>
    t(`boards.template-setup.marketing.alternative-description`),
  'boards.template-setup.web-analytics.alternative-description': ({t}) =>
    t(`boards.template-setup.web-analytics.alternative-description`),
  'boards.template-setup.saas-company-kpi.alternative-description': ({t}) =>
    t(`boards.template-setup.saas-company-kpi.alternative-description`),
  'boards.template-setup.ai-company-kpi.alternative-description': ({t}) =>
    t(`boards.template-setup.ai-company-kpi.alternative-description`),
};

export interface ApiBlueprintTemplate {
  alternative_description_key: string;
  description_key: string;
  number_of_reports: number;
  title_key: string;
}

export interface BlueprintsAllResponse {
  status: `ok`;
  templates: {
    // these are the same as TemplateType. perhaps in the future, remove TemplateSpecs in favor of the API
    [templateType: string]: ApiBlueprintTemplate;
  };
}

export const TemplateSpecs: Record<TemplateSpecsKeys, TemplateSpecsEntry> = {
  [TemplateType.KPI]: {
    title: `Company KPIs`,
    type: TemplateType.KPI,
    imageId: `kpi`,
    demoBoardLink: `https://mixpanel.com/p/QLBHa24vdYuK2MJLiNUA1S`,
    requiredFields: [KPITemplateFields.SIGN_UP, KPITemplateFields.VALUE_MOMENT],
    keywords: [
      `Company`,
      `KPI`,
      `KPIs`,
      `Key Performance Indicators`,
      `Engagement`,
      `Retention`,
      `Growth`,
      `Core Performance Indicators`,
      `Core Metrics`,
      `DAU`,
      `MAU`,
      `WAU`,
      `Starter Template`,
      `Template`,
    ],
  },
  [TemplateType.MARKETING_KPI]: {
    title: `Marketing KPIs`,
    type: TemplateType.MARKETING_KPI,
    imageId: `marketing-kpi`,
    demoBoardLink: `https://mixpanel.com/p/MKDgQSoYBZciN4AGgY7Mgh`,
    requiredFields: [MarketingKPITemplateFields.CONVERSION_EVENT],
    keywords: [
      `Marketing`,
      `KPIs`,
      `Key Performance Indicators`,
      `User base`,
      `Marketing Campaign`,
      `Activation`,
      `Conversion`,
      `Acquisition`,
      `Session engagement`,
      `Conversion journey`,
      `Template`,
    ],
  },
  [TemplateType.WEB_ANALYTICS]: {
    title: `Web Analytics`,
    type: TemplateType.WEB_ANALYTICS,
    imageId: `web-analytics`,
    demoBoardLink: `https://mixpanel.com/project/3018488/view/3536632/app/boards#id=6020908`,
    requiredFields: [WebAnalyticsTemplateFields.PAGE_LOADED_EVENT, WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY],
    keywords: [`Web Analytics`, `Website Metrics`, `Template`],
  },
  [TemplateType.FEATURE_LAUNCH]: {
    title: `Feature Launch`,
    type: TemplateType.FEATURE_LAUNCH,
    imageId: `feature-launch`,
    demoBoardLink: `https://mixpanel.com/p/5dbqyToAtFMaDJVJNzUeuG`,
    requiredFields: [
      ProductLaunchTemplateFields.FEATURE_NAME,
      ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
      ProductLaunchTemplateFields.LAUNCH_DATE,
      ProductLaunchTemplateFields.NEW_FEATURE,
    ],
    keywords: [
      `Feature`,
      `Launch`,
      `Product Launch`,
      `Feature Release`,
      `Product release`,
      `New Feature`,
      `Launching Feature`,
      `Starter Template`,
      `Template`,
      `Feature adoption`,
      `Activation rate`,
      `Template`,
    ],
  },
  [TemplateType.AI_COMPANY_KPI]: {
    title: `AI Company KPIs`,
    type: TemplateType.AI_COMPANY_KPI,
    imageId: `ai-company-kpis`,
    requiredFields: [],
    keywords: [
      `Company`,
      `AI`,
      `LLM`,
      `Tokens`,
      `Engagement`,
      `Retention`,
      `Growth`,
      `API Cost`,
      `Chatbots`,
      `Starter Template`,
      `Template`,
    ],
  },
  [TemplateType.E_COMMERCE]: {
    title: `Ecommerce`,
    type: TemplateType.E_COMMERCE,
    imageId: `e-commerce`,
    demoBoardLink: ` https://mixpanel.com/project/3018488/view/3536632/app/boards#id=8229971`,
    requiredFields: [
      ECommerceTemplateFields.ADD_TO_CART_EVENT,
      ECommerceTemplateFields.CART_PROPERTY,
      ECommerceTemplateFields.CATEGORY_KEY,
      ECommerceTemplateFields.PRICE_KEY,
      ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
      ECommerceTemplateFields.PURCHASE_EVENT,
    ],
    keywords: [`Ecommerce`, `E-Commerce`, `Purchase`, `Retail`, `Cart analysis`, `Template`],
  },
  [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: {
    title: `Lifecycle Cohort Analysis`,
    type: TemplateType.LIFECYCLE_COHORT_ANALYSIS,
    imageId: `lifecycle-cohort-analysis`,
    demoBoardLink: `https://mixpanel.com/p/CEngFwLvPa5zvTSLvFTHNH`,
    requiredFields: [LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT],
    keywords: [
      `LCA`,
      `Lifecycle`,
      `Cohort`,
      `Cohort analysis`,
      `User template`,
      `User journey`,
      `User segmentation`,
      `Value Moment`,
      `Value Moment Analysis`,
      `Resurrected User`,
      `Dormant user`,
      `Retained user`,
      `new user`,
      `Template`,
    ],
  },
  [TemplateType.SAAS_COMPANY_KPI]: {
    title: `B2B Account KPIs`,
    type: TemplateType.B2B,
    imageId: `saas-company-kpis`,
    demoBoardLink: `https://mixpanel.com/project/3409416/view/3914056/app/boards#id=8230017`,
    keywords: [],
    requiredFields: [
      SaaSCompanyKpiTemplateFields.DATA_GROUP_ID,
      SaaSCompanyKpiTemplateFields.MRR_PROPERTY_FIELD,
      SaaSCompanyKpiTemplateFields.PLAN_TYPE_PROPERTY_FIELD,
      SaaSCompanyKpiTemplateFields.SEGMENT_PROPERTY_FIELD,
    ],
  },
  [TemplateType.B2B]: {
    title: `B2B Account KPIs`,
    type: TemplateType.B2B,
    imageId: `kpi`,
    demoBoardLink: `https://mixpanel.com/p/QLBHa24vdYuK2MJLiNUA1S`,
    requiredFields: [
      B2BTemplateFields.DATA_GROUP_ID,
      B2BTemplateFields.ACTIVATION_EVENT,
      B2BTemplateFields.ARR_PROPERTY_FIELD,
    ],
    keywords: [
      `Company`,
      `KPI`,
      `KPIs`,
      `Key Performance Indicators`,
      `Engagement`,
      `Retention`,
      `Growth`,
      `Core Performance Indicators`,
      `Core Metrics`,
      `DAU`,
      `MAU`,
      `WAU`,
      `Starter Template`,
      `Template`,
    ],
    featureRolloutName: FeatureRolloutName.WarehouseLaunchTemplates,
  },
};

type CreatorEditorInfo = {
  created?: string;
  creator_id: null | number;
  creator_name: null | string; // full name of creator
  creator_email: null | string;

  modified?: string;
  last_modified_by_id?: number;
  last_modified_by_email?: string;
  last_modified_by_name?: string;
};

// Bookmark format returned by the dashboards and bookmarks API
export type Bookmark<BookmarkParamsType extends BookmarkParamsUnionType = TODO> = CreatorEditorInfo & {
  dashboard: DashboardOrigin;
  id: number;
  is_default: boolean;
  name: string;
  description?: string;
  original_type: string;
  params: BookmarkParamsType;
  project_id: number;
  icon?: string;
  workspace_id?: number;
  type: BookmarkType;
  error?: string;
  metadata?: any;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;

  unique_view_count?: number;
  total_view_count?: number;
};

// the only necessary fields needed to persist
export type DisplayBookmark<BookmarkParamsType extends BookmarkParamsUnionType = TODO> = Pick<
  Bookmark<BookmarkParamsType>,
  `params` | `type` | `name` | `description`
>;

export interface DashboardCardChartOptions {
  type?: ChartType;
}

export type CardOnDashboard =
  | DashboardCard
  | TextCard
  | MediaCard
  | NestedDashboardCard
  | BenchmarkCard
  | HeatmapCard
  | PlaylistCard;

export type SeenCards = Record<DashboardCardId, boolean>;

//TODO @shayan, since we added content_id, remove duplicate id types from cards after GA

// report or report link type card on the dashboard
export interface DashboardCard<BookmarkParamsType extends BookmarkParamsUnionType = TODO> {
  id: DashboardCardId;
  title?: string;
  description?: string;
  width: number;
  height: number;
  bookmark?: Bookmark<BookmarkParamsType>;
  bookmark_metadata?: any;
  chart_options?: DashboardCardChartOptions;
  linked_dashboard_card_id?: number;
  type?: Exclude<ContentType, ContentType.Spark>;
  source_bookmark_id?: number;
  content_id?: number | string;
  auto_pinned?: boolean | null;
}

export interface NestedDashboardCard {
  id: DashboardCardId;
  width: number;
  height: number;
  type: ContentType.Dashboard;
  content_id: number;
  title: string;
  description: string;
  last_edited: string;
  thumbnail_layout: LayoutResponse;
  thumbnail_contents: ContentsResponse;
}

export interface HeatmapCard {
  id: string;
  width: number;
  height: number;
  type: ContentType.Heatmap | ContentType.HeatmapCollection;
  content_id: string;
  title: string;
}

export interface PlaylistCard {
  id: string;
  name: string;
  width: number;
  height: number;
  type: ContentType.Playlist;
  content_id: string;
}

export interface TextCard {
  id: DashboardCardId;
  width: number;
  height: number;
  markdown: string;
  type?: Exclude<ContentType, ContentType.Spark>;
  text_card_id?: number;
  content_id?: number;
}

export interface BenchmarkCard<BookmarkParamsType extends BookmarkParamsUnionType = TODO> {
  id: DashboardCardId;
  width: number;
  height: number;
  benchmark_type: string;
  type: ContentType.Benchmark;
  industry: string;
  bookmark?: Bookmark<BookmarkParamsType>;
  bookmark_metadata?: any;
  content_id?: number;
}

export enum ContentType {
  ReportLink = `report-link`,
  Report = `report`,
  Text = `text`,
  Media = `media`,
  Dashboard = `dashboard`,
  // For when an entire row is duplicated
  Row = `row`,
  Heatmap = `heatmap`,
  HeatmapCollection = `heatmap-collection`,
  // Special frontend only card. It is filtered out when saving.
  Spark = `spark`,
  Benchmark = `benchmark`,
  Playlist = `playlist`,
}

export enum ContentActionType {
  Move = `move`,
  Delete = `delete`,
  Duplicate = `duplicate`, // for report/report-links, creates corresponding report
  Create = `create`,
  Update = `update`,
  Undelete = `undelete`,
  UnMove = `unmove`,
  CreateReportLink = `create-report-link`, // only for report/report-links
}

export interface ContentAction {
  actionType: ContentActionType;
  contentId?: number | string;
  contentType: ContentType;
  contentParams?: TODO;
}

export enum LayoutActionType {
  RearrangeRow = `rearrange-row`,
  RearrangeMultiRow = `rearrange-multi-row`,
  RearrangeNewRow = `rearrange-new-row`,
  Resize = `resize`,
  MoveRow = `move-row`,
  CombineRows = `combine-rows`,
}

export interface LayoutAction {
  actionType: LayoutActionType;
}

/*
  Must match MediaCardType in dashboard/models.py
*/
export enum MediaCardType {
  Image = `image`,
  Video = `video`,
  EmbeddedImage = `embed_image`,
  EmbeddedVideo = `embed_video`,
}

export interface MediaCard {
  id: DashboardCardId;
  width: number;
  height: number;
  type?: Exclude<ContentType, ContentType.Spark>;
  media_card_id?: number;
  media_type: MediaCardType;
  service: string;
  path: string;
  content_id?: number;
}

export enum DashboardType {
  UserCreated = `User Created`,
  TemplateGenerated = `Template Generated`,
  MyDashboard = `My Dashboard`,
  StagedTemplate = `Staged Template`,
}

export type Dashboard = CreatorEditorInfo & {
  id: number;
  title: string;
  description: null | string;
  bookmarks?: Bookmark[];
  card_order: DashboardCardId[];
  cards: CardOnDashboard[];
  time_filter: TimeFilter;
  filters: PersistedBoardFilter[];
  breakdowns?: Segmentation[];
  generation_type?: DashboardType;
  template_type?: TemplateType;

  new_content?: TODO;

  // permissions
  can_view: boolean;
  can_update_basic: boolean;
  can_share: boolean;
  is_superadmin: boolean;
  allow_staff_override: boolean;
  can_pin?: boolean;

  is_favorited?: boolean;
  pinned_date?: string;
  num_active_public_links?: number;
  project_id?: number;
  is_shared_with_project?: boolean;

  unique_view_count?: number;
  total_view_count?: number;

  layout: LayoutResponse;
  contents: ContentsResponse;

  parent_dashboard_id?: number;
  ancestors?: Array<Pick<Dashboard, `id` | `title`>>;
  child_dashboards?: Dashboard[];
};

export type TimeFilter =
  | {
      displayText: Translatable;
      dateRange: DateRange;
    }
  | EmptyObj;

export enum DashboardQueryOrigin {
  Standard = `dashboard`,
  Digest = `dashboard_digest`,
  Public = `dashboard_public`,
  TvMode = `dashboard_tv_mode`,
  Home = `home_app`,
  HomeDashboard = `dashboard_home_app`,
}

export enum BadLayoutRequestErrors {
  // the string values are defined in app_api
  InvalidLayout = `INVALID_LAYOUT`,
  TooManyMediaCardsOnDashboard = `TOO_MANY_DASH_MEDIA_CARDS`,
  TooManyMediaCardsInProject = `TOO_MANY_PROJECT_MEDIA_CARDS`,
}

export const BadLayoutRequestMessages = {
  [BadLayoutRequestErrors.InvalidLayout]: `Changes could not be saved due to a change conflict.`,
  [BadLayoutRequestErrors.TooManyMediaCardsOnDashboard]: `Changes could not be saved. This board has reached its media card limit.`,
  [BadLayoutRequestErrors.TooManyMediaCardsInProject]: `Changes could not be saved. This project has reached its media card limit.`,
};

export type LayoutCell = {
  id: string;
  content_id: string;
  content_type: ContentType;
  width: number;
};

export interface LayoutRowResponse {
  height: number;
  cells: LayoutCell[];
}

export type LayoutResponse =
  | EmptyObj
  | {
      version: string;
      order: string[];
      rows: {[id: string]: LayoutRowResponse};
    };

type BaseContent = {
  creator: string;
  creator_id: number;
  creator_name: string;
  creator_email: string;
  dashboard_id: number;
  id: number;
};

export type MediaContent = BaseContent & {
  media_type: MediaCardType;
  path: string;
  service: string;
};

export type TextContent = BaseContent & {
  markdown: string;
};

export type BenchmarkContent<BookmarkParamsType = TODO> = BaseContent & {
  benchmark_type: string;
  industry: string;
  params: BookmarkParamsType;
};

export type ReportContent = Bookmark;

export type ReportLinkContent = {
  id: number;
  bookmark: Bookmark;
};

export type HeatMapContent = Pick<HeatMap, `id` | `url`>;

export type HeatMapCollectionContent = Pick<HeatMapCollection, `id`>;

export type PlaylistContent = BaseContent & {
  id: string;
  name: string;
};

type NestedDashboardContent = Pick<
  Dashboard,
  `id` | `description` | `last_modified_by_email` | `last_modified_by_id` | `last_modified_by_name` | `creator_id`
> & {last_edited: string; thumbnail_layout: LayoutResponse; thumbnail_contents: ContentsResponse};

export type ContentsMapById<Content> = {
  [id: number]: Content;
};

export type ContentsResponse =
  | EmptyObj
  | {
      [ContentType.Media]: ContentsMapById<MediaContent>;
      [ContentType.Text]: ContentsMapById<TextContent>;
      [ContentType.Report]: ContentsMapById<ReportContent>;
      [ContentType.ReportLink]: ContentsMapById<ReportLinkContent>;
      [ContentType.Dashboard]: ContentsMapById<NestedDashboardContent>;
      [ContentType.Benchmark]: ContentsMapById<BenchmarkContent>;
      [ContentType.Heatmap]: ContentsMapById<HeatMapContent>;
      [ContentType.HeatmapCollection]: ContentsMapById<HeatMapCollectionContent>;
      [ContentType.Playlist]: ContentsMapById<PlaylistContent>;
    };

export type CardContent =
  | ReportContent
  | ReportLinkContent
  | TextContent
  | MediaContent
  | BenchmarkContent
  | HeatMapContent
  | HeatMapCollectionContent
  | PlaylistContent;

export type BlueprintCohort = {
  definition: {
    groups: CohortGroupEntry[];
  };
  name: string;
};

export type BlueprintConfig = {
  blueprint_id: number;
  variables: {
    // event name -> blueprint name (blueprint name will be replaces)
    // example: "Purchase": "MP Blueprint Event 5",
    [eventName: string]: string;
  };
  eventNameMap: {
    // blueprint name -> real name
    // example: "MP Blueprint Event 5": "Purchase Completed",
    [blueprintName: string]: string;
  };
  cohorts: {
    [id: number]: BlueprintCohort;
  };
  scdPropEventMap: {
    [scdPropName: string]: string;
  };
};

export interface Trackable {
  toTrackingData(): Obj;
}

export type DataGroupNotSupportedReason = string | DataGroupNotSupportedReasonCode;

export enum DataGroupNotSupportedReasonCode {
  UserFilterOnDataGroup = `user_filter_on_data_group`,
  DataGroupIdMisMatch = `data_group_id_mismatch`,
}
